import { capitalize } from '@material-ui/core'
import { TableCellWithAdditionalCountProps } from 'components/shared'
import { SelectOption } from 'components/shared/Select'
import { action, computed, observable } from 'mobx'
import { ProjectDTO, ProjectDTONameAndId, User, UserDTO, UserRoleDTO } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { PaginationSearchBase } from 'store/PaginationSearchBase'
import { RootStore } from 'store/RootStore'
import { Api, getSortingResult } from 'utilities'
import {
  initialUsersTablePaginationProps,
  initialUsersTableSearchProps,
  UserProjects,
  usersTableNeedTimeoutProps,
  UsersTableSearchProps,
  UsersTableSortProps
} from './UsersTableStore.types'

export const USERS_TABLE_STORE = 'USERS_TABLE_STORE'

export class UsersTableStore extends PaginationSearchBase<
  UsersTableSortProps,
  UsersTableSearchProps,
  User
  > {
  private _rootStore: RootStore
  private _users: User[] = []
  private _projects: ProjectDTONameAndId = null

  constructor (rootStore: RootStore) {
    super(
      initialUsersTablePaginationProps,
      initialUsersTableSearchProps,
      [],
      usersTableNeedTimeoutProps
    )
    this._rootStore = rootStore
  }

  @observable userRoles: UserRoleDTO[] = []

  @action setUserRoles = (userRoles: UserRoleDTO[]) => {
    this.userRoles = userRoles
  }

  private _getProjectsByUserId = (userId: number): ProjectDTO[] => {
    if (!this._projects) return []

    return this._projects[`U${userId}`] || []
  }

  getUsers = () => {
    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this._rootStore[GENERAL_STORE]

    setLoadingOn()

    Promise.all([
      Api.get<UserDTO[]>('/users/all', 'userApi'),
      Api.get<ProjectDTONameAndId>('/permissions/users/projects')
    ])
      .then(([users, projects]) => {
        const roleMap = new Map<number, UserRoleDTO>()
        this._users = users.map(u => {
          (u.roles || []).forEach(role => roleMap.set(role.id, role))
          return new User(u)
        })
        this._projects = projects
        this.setUserRoles(Array.from(roleMap.values()))
        this.triggerSearch()
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  triggerSearch = () => {
    const {
      name,
      role,
      projects,
      addedOnPeriod,
      addedBy
    } = this.searchProps

    const applyFilters = (user: User): boolean => {
      const nameFilter = name
        ? user.name.toLowerCase().includes(name.trim().toLowerCase())
        : true
      const roleFilter = role
        ? Boolean(user.roles.find(r => r.id === role))
        : true
      const projectFilter = projects
        ? this._getProjectsByUserId(user.id).some(p =>
          p.name.toLowerCase().includes(projects.trim().toLowerCase())
        )
        : true
      const addedOnFilter = addedOnPeriod
        ? user.created.isBetween(
          addedOnPeriod.start,
          addedOnPeriod.end,
          undefined,
          '[]'
        )
        : true
      const addedByFilter = addedBy
        ? user.createdBy
          ? user.createdBy.name.toLowerCase()
            .includes(addedBy.trim().toLowerCase())
          : false
        : true

      return (
        nameFilter &&
        roleFilter &&
        projectFilter &&
        addedOnFilter &&
        addedByFilter
      )
    }

    const results = this._users.filter(applyFilters)

    const { sortProp, sortDirection } = this.pagination
    results.sort((a, b) => {
      let valueA, valueB

      switch (sortProp) {
        case UsersTableSortProps.NAME:
          valueA = a.name
          valueB = b.name
          break
        case UsersTableSortProps.ROLE:
          valueA = a.rolesAsString
          valueB = b.rolesAsString
          break
        case UsersTableSortProps.ADDED_ON:
          valueA = a.created
          valueB = b.created
          break
        case UsersTableSortProps.ADDED_BY:
          valueA = a.createdBy ? a.createdBy.name : ''
          valueB = b.createdBy ? b.createdBy.name : ''
          break
        default: break
      }

      return getSortingResult(valueA, valueB, sortDirection)
    })

    this.setSearchResults(results)
  }

  @computed get userRoleSelectOptions (): SelectOption<number>[] {
    return this.userRoles.map(role => ({
      label: capitalize(role.label.toLowerCase()),
      value: role.id
    }))
  }

  getProjectsInfo = (userId: number): TableCellWithAdditionalCountProps => {
    const projects = this._getProjectsByUserId(userId)
    return {
      text: projects.length ? projects[0]?.name : '',
      totalCount: projects.length
    }
  }
}
